import { useEffect, useState } from 'react'
import { Button } from "../../ui/button"
import { ArrowRight, X } from 'lucide-react'
import { motion } from 'framer-motion'

interface WelcomeModalProps {
  isOpen: boolean
  onClose: () => void
  onGetStarted: () => void
}

const ConfettiParticle = ({ delay }: { delay: number }) => {
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981']
  const color = colors[Math.floor(Math.random() * colors.length)]
  const size = Math.random() * 8 + 4
  const initialX = Math.random() * 100
  const drift = (Math.random() - 0.5) * 50
  return (
    <motion.div
      className="absolute rounded-full"
      style={{ backgroundColor: color, width: size, height: size, left: `${initialX}%` }}
      initial={{ y: -20, opacity: 1, rotate: 0 }}
      animate={{ y: 400, opacity: 0, rotate: 360, x: drift }}
      transition={{ duration: 2.5, delay, ease: 'easeOut' }}
    />
  )
}

const Confetti = ({ show }: { show: boolean }) => {
  const [particles, setParticles] = useState<number[]>([])
  useEffect(() => {
    if (show) {
      const newParticles = Array.from({ length: 30 }, (_, i) => i)
      setParticles(newParticles)
      const timer = setTimeout(() => setParticles([]), 3000)
      return () => clearTimeout(timer)
    }
  }, [show])
  if (!show || particles.length === 0) return null
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((id) => (
        <ConfettiParticle key={id} delay={Math.random() * 0.5} />
      ))}
    </div>
  )
}

export function WelcomeModal({ isOpen, onClose, onGetStarted }: WelcomeModalProps) {
  const [showConfetti, setShowConfetti] = useState(false)
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowConfetti(true), 200)
      return () => clearTimeout(timer)
    } else {
      setShowConfetti(false)
    }
  }, [isOpen])

  const handleGetStarted = () => {
    onGetStarted()
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay with blur */}
      <div
        className="fixed inset-0 bg-black/70 dark:bg-black/70 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in-0 duration-300"
        onClick={onClose}
      >
        {/* Modal content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl shadow-black/10 dark:shadow-black/30 overflow-hidden max-w-md w-full mx-4 animate-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <Confetti show={showConfetti} />
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="px-8 py-10 text-center space-y-6">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Welcome aboard ✨</h1>
            </div>
            <p className="text-base text-muted-foreground leading-relaxed max-w-xs mx-auto">
              Your AIRA workspace is ready — time to make things happen.
            </p>
            <div className="pt-2">
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="h-12 px-8 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white transition-all duration-200 hover:scale-[1.02] focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 relative group/btn shadow-lg hover:shadow-xl"
              >
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-200 blur-lg -z-10"></div>
                <span className="flex items-center gap-2">Let's go<ArrowRight className="h-4 w-4" /></span>
              </Button>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 via-purple-50/20 to-pink-50/30 dark:from-slate-800/30 dark:via-slate-700/20 dark:to-slate-800/30 -z-10"></div>
        </motion.div>
      </div>
    </>
  )
}


